import { useEffect, useState } from 'react';
import { analyticsAPI, ordersAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { DollarSign, ShoppingCart, TrendingUp, Clock } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import { getOrderItemCount } from '../../lib/utils';

export default function AdminDashboard() {
  const [revenue, setRevenue] = useState(null);
  const [ordersData, setOrdersData] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [revenueRes, ordersRes, recentRes] = await Promise.all([
          analyticsAPI.getRevenue(),
          analyticsAPI.getOrders(),
          ordersAPI.getAll({ limit: 5 })
        ]);
        setRevenue(revenueRes.data);
        setOrdersData(ordersRes.data);
        setRecentOrders(recentRes.data);
      } catch (err) {
        console.error('Error loading dashboard:', err);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, []);

  const formatHourlyData = () => {
    if (!ordersData?.orders_per_hour) return [];
    return Object.entries(ordersData.orders_per_hour).map(([hour, count]) => ({
      hour,
      commandes: count
    }));
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <div className="animate-pulse space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-32 bg-gray-200 rounded-xl" />
            ))}
          </div>
          <div className="h-80 bg-gray-200 rounded-xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#111827]" style={{ fontFamily: 'Manrope' }}>
          Dashboard
        </h1>
        <p className="text-gray-500">Vue d'ensemble de votre activité</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Revenus aujourd'hui</p>
                <p className="text-2xl font-bold" style={{ fontFamily: 'JetBrains Mono' }}>
                  {revenue?.today_revenue?.toFixed(2)}€
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-violet-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Commandes aujourd'hui</p>
                <p className="text-2xl font-bold" style={{ fontFamily: 'JetBrains Mono' }}>
                  {ordersData?.orders_today || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <ShoppingCart className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Revenus semaine</p>
                <p className="text-2xl font-bold" style={{ fontFamily: 'JetBrains Mono' }}>
                  {revenue?.weekly_revenue?.toFixed(2)}€
                </p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Panier moyen</p>
                <p className="text-2xl font-bold" style={{ fontFamily: 'JetBrains Mono' }}>
                  {ordersData?.average_order_value?.toFixed(2)}€
                </p>
              </div>
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Manrope' }}>Commandes par heure</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={formatHourlyData()}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="hour" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip 
                    contentStyle={{ 
                      borderRadius: '8px', 
                      border: 'none', 
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)' 
                    }} 
                  />
                  <Bar dataKey="commandes" fill="#7C3AED" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Manrope' }}>Dernières commandes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {recentOrders.map((order) => (
                <div key={order.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Badge variant="outline">Table {order.table_number}</Badge>
                    <span className="text-sm text-gray-500">
                      {getOrderItemCount(order)} article{getOrderItemCount(order) > 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-semibold" style={{ fontFamily: 'JetBrains Mono' }}>
                      {order.total_price?.toFixed(2)}€
                    </span>
                    <Badge 
                      className={
                        order.status === 'delivered' ? 'bg-gray-500' :
                        order.status === 'ready' ? 'bg-blue-500' :
                        order.status === 'preparing' ? 'bg-amber-500' :
                        'bg-blue-500'
                      }
                    >
                      {order.status}
                    </Badge>
                  </div>
                </div>
              ))}
              
              {recentOrders.length === 0 && (
                <p className="text-center text-gray-400 py-8">Aucune commande récente</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
